import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import SavedFilter from './saved-filter';
import IconUp from '../icons/up';
import IconDown from '../icons/down';
import IconExpand from '../icons/expand';
import IconDots from '../icons/dots';
import IconDownload from '../icons/download';
import IconSettings from '../icons/settings';
import IconTrash from '../icons/trash';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import ButtonSecondary from '../button/button-secondary';
import CheckboxInput from '../checkbox/checkbox';
import { ButtonSize } from '../button/types';
import ButtonPseudo from '../button/button-pseudo';
import { SavedFiltersComponentTypes, SchemaFilterType } from './types';
import usePrevious from '../../hooks/use-previous';

import './table.css';

export default function SavedFilters({
  savedFiltersKey,
  savedFilters,
  setSavedFilters,
  userId,
  selectedSavedFilter,
  onSavedFilterClick,
  onFilterChange,
  filters,
  setSelectedSavedFilter,
  columnsLabels,
  filtersComponent,
  setSaveFilterPopupVisible,
  onUploadFiltersPreset,
  uploadFiltersPresetRef,
}: SavedFiltersComponentTypes) {
  const [saveFilterMenuVisible, setSaveFilterMenuVisible] = useState(false);
  const [savedFiltersExpanded, setSavedFilterExpanded] = useState(false);
  const [savedFiltersSettingsVisible, setSavedFiltersSettingsVisible] =
    useState(false);
  const [selectedFiltersPresets, setSelectedFiltersPresets] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);

  const prevFilters = usePrevious(filters);

  const onDeleteSelectedPresets = () => {
    if (
      selectedFiltersPresets?.find((item) => item.id === selectedSavedFilter) &&
      setSelectedSavedFilter
    ) {
      setSelectedSavedFilter('');

      onFilterChange({
        cursor: filters?.cursor,
      });
    }

    const savedFiltersJSON = localStorage.getItem(savedFiltersKey as string);

    if (savedFiltersJSON) {
      const parsed: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
        employeeId: string;
      }[] = JSON.parse(savedFiltersJSON);

      const nextSavedFilters = parsed
        ?.filter(
          (filterItem) =>
            selectedFiltersPresets?.every((item) => filterItem.id !== item.id),
        )
        ?.map((filterItem, index) => ({
          ...filterItem,
          id: `${savedFiltersKey}-${index + 1}`,
        }));

      const currentUserSavedFilters = nextSavedFilters?.filter(
        (filterItem) => filterItem.employeeId === userId,
      );

      if (setSavedFilters) {
        setSavedFilters(
          currentUserSavedFilters,
        );
      }

      if (nextSavedFilters?.length) {
        localStorage.setItem(
          savedFiltersKey as string,
          JSON.stringify(nextSavedFilters),
        );
      } else {
        localStorage.removeItem(savedFiltersKey as string);
      }

      setSelectedFiltersPresets([]);
    }
  };

  const onRewriteFilter = (id: string) => {
    const nextSavedFilters = savedFilters?.map((filterItem) => {
      const result = { ...filterItem };

      if (result.id === id) {
        result.value = filters;
      }

      return result;
    });

    if (setSelectedSavedFilter) {
      setSelectedSavedFilter(id);
    }

    localStorage.setItem(
      savedFiltersKey as string,
      JSON.stringify(nextSavedFilters),
    );

    if (setSavedFilters) {
      setSavedFilters(
        nextSavedFilters as {
          id: string;
          name: string;
          value: Record<string, any>;
          default?: boolean;
          employeeId: string;
        }[],
      );
    }
  };

  const onDownloadPreset = (id: string) => {
    const currentSavedFilter = savedFilters?.find((item) => item.id === id);

    if (currentSavedFilter) {
      const fileContent = {
        key: savedFiltersKey as string,
        values: [currentSavedFilter],
      };

      const blob = new Blob([JSON.stringify(fileContent)], {
        type: 'text/plain',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      const savedFiltersName = savedFiltersKey
        ?.split('-')
        ?.map((key) => `${key.charAt(0).toUpperCase()}${key.slice(1)}`)
        ?.join(' ');

      a.href = url;
      a.download = `${savedFiltersName} ${currentSavedFilter.name} preset`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const onDownloadPresets = () => {
    const fileContent: {
      key: string;
      values?: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
        employeeId: string;
      }[];
    } = {
      key: savedFiltersKey as string,
    };

    if (selectedFiltersPresets?.length === savedFilters?.length) {
      const savedFiltersJSON = localStorage.getItem(savedFiltersKey as string);

      if (savedFiltersJSON) {
        const parsed = JSON.parse(savedFiltersJSON);

        if (parsed) {
          fileContent.values = parsed?.filter(
            (filterItem: any) => filterItem.employeeId === userId,
          );
        }
      }
    } else {
      fileContent.values = selectedFiltersPresets;
    }

    const blob = new Blob([JSON.stringify(fileContent)], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    const savedFiltersName = savedFiltersKey
      ?.split('-')
      ?.map((key) => `${key.charAt(0).toUpperCase()}${key.slice(1)}`)
      ?.join(' ');

    a.href = url;
    a.download = `${savedFiltersName} Preset`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSavedFiltersSettingsVisible(false);
    setSelectedFiltersPresets([]);
  };

  useEffect(() => {
    if (
      savedFilters &&
      savedFilters.length &&
      Object.keys(filters)?.length &&
      filters &&
      !isEqual(filters, prevFilters) &&
      !selectedSavedFilter
    ) {
      const formattedSavedFilters = savedFilters?.map((item) => {
        const result = { ...item };

        const nextValues: Record<string, { value: string[]; type: string }> =
          {};

        Object.keys(item.value)?.forEach((key) => {
          const valueResult = { ...item.value[key] };

          const sortedValue = valueResult.value.sort((a: string, b: string) =>
            a.localeCompare(b),
          );

          nextValues[key] = {
            value: sortedValue,
            type: valueResult.type,
          };
        });

        result.value = nextValues;

        return result;
      });

      const nextFilters: Record<string, { value: string[]; type: SchemaFilterType }> = {};

      Object.keys(filters)?.forEach((key) => {
        const valueResult = { ...filters[key] };

        const sortedValue = valueResult.value.sort((a: string, b: string) =>
          a.localeCompare(b),
        );

        nextFilters[key] = {
          value: sortedValue,
          type: valueResult.type as SchemaFilterType,
        };
      });

      const filtersEqualSaved = formattedSavedFilters?.find((item) =>
        isEqual(item.value, nextFilters),
      );

      if (filtersEqualSaved && setSelectedSavedFilter) {
        setSelectedSavedFilter(filtersEqualSaved.id);
      }
    }
  }, [filters, savedFilters, selectedSavedFilter, prevFilters]);

  return (
    <>
      {savedFiltersKey && (
        <ClickOutsideDetector
          className="saved-filters-settings-wrapper"
          setOptionsListVisible={() => {
            setSavedFiltersSettingsVisible(false);
            setSelectedFiltersPresets([]);
          }}
        >
          <>
            <button
              type="button"
              aria-label="Saved filter settings"
              className={`saved-filters-settings-button ${
                savedFiltersSettingsVisible ? 'active' : ''
              }`}
              onClick={() => {
                if (!savedFiltersSettingsVisible) {
                  setSavedFiltersSettingsVisible(true);
                } else {
                  setSavedFiltersSettingsVisible(false);
                  setSelectedFiltersPresets([]);
                }
              }}
            >
              <IconSettings />
            </button>
            {savedFiltersSettingsVisible && (
              <div className="saved-filters-settings-list">
                <div className="saved-filters-settings-list-top">
                  <div className="saved-filters-settings-list-title">
                    Preset filters manage
                  </div>
                  <CheckboxInput
                    type="checkbox"
                    checked={Boolean(
                      savedFilters?.length &&
                        selectedFiltersPresets?.length === savedFilters?.length,
                    )}
                    disabled={!savedFilters?.length}
                    title="Select all"
                    name="select-all-filters"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedFiltersPresets(
                          savedFilters as {
                            id: string;
                            name: string;
                            value: Record<string, any>;
                            default?: boolean;
                            employeeId: string;
                          }[],
                        );
                      } else {
                        setSelectedFiltersPresets([]);
                      }
                    }}
                    visualClassName="select-all-saved-filters"
                  />
                </div>
                <div className="upload-saved-filters-presets-wrapper">
                  <button
                    type="button"
                    className="upload-saved-filters-presets-button"
                    onClick={() => {
                      uploadFiltersPresetRef?.current?.click();
                    }}
                  >
                    <IconExpand width={18} height={18} />
                    Import Filter Preset
                  </button>
                  <input
                    type="file"
                    className="upload-input"
                    ref={uploadFiltersPresetRef}
                    onChange={(e) =>
                      onUploadFiltersPreset(e.target.files as FileList)
                    }
                    accept=".txt"
                  />
                </div>
                <div className="saved-filters-settings-list-content">
                  {savedFilters?.map((item) => (
                    <div
                      className="saved-filters-settings-list-item"
                      key={item.id}
                    >
                      <div className="saved-filters-settings-list-item-checkbox">
                        <CheckboxInput
                          type="checkbox"
                          checked={Boolean(
                            selectedFiltersPresets?.find(
                              (selected) => selected.id === item.id,
                            ),
                          )}
                          title={item.name}
                          name={`${savedFiltersKey}-${item.id}`}
                          visualClassName="saved-filters-settings-list-item-visual"
                          onChange={() => {
                            let nextSelectedPresets = (
                              [] as {
                                id: string;
                                name: string;
                                value: Record<string, any>;
                                default?: boolean;
                                employeeId: string;
                              }[]
                            ).concat(selectedFiltersPresets);

                            if (
                              nextSelectedPresets.find(
                                (selected) => selected.id === item.id,
                              )
                            ) {
                              nextSelectedPresets = nextSelectedPresets.filter(
                                (selected) => selected.id !== item.id,
                              );
                            } else {
                              nextSelectedPresets.push(item);
                            }

                            setSelectedFiltersPresets(nextSelectedPresets);
                          }}
                        />
                      </div>
                      <button
                        className="saved-filters-settings-list-item-download"
                        type="button"
                        aria-label="Download preset"
                        onClick={() => onDownloadPreset(item.id)}
                      >
                        <IconDownload />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="saved-filters-settings-list-bottom">
                  <ButtonSecondary
                    text={
                      selectedFiltersPresets?.length
                        ? `Export ${selectedFiltersPresets?.length} presets`
                        : 'Export'
                    }
                    icon={<IconDownload />}
                    size={ButtonSize.SMALL}
                    onClick={onDownloadPresets}
                    disabled={!selectedFiltersPresets.length}
                    className="export-presets-button"
                  />
                  <ButtonPseudo
                    text="Delete"
                    className="delete-presets-button"
                    size={ButtonSize.SMALL}
                    icon={<IconTrash width={16} height={16} />}
                    disabled={!selectedFiltersPresets?.length}
                    onClick={onDeleteSelectedPresets}
                  />
                </div>
              </div>
            )}
          </>
        </ClickOutsideDetector>
      )}
      {savedFilters &&
        savedFilters?.length > 0 &&
        savedFilters?.slice(0, 4)?.map((item, index) => (
          <SavedFilter
            // eslint-disable-next-line react/no-array-index-key
            key={`${item.name}-${index}`}
            item={item}
            selectedSavedFilter={selectedSavedFilter}
            onSavedFilterClick={onSavedFilterClick}
            onFilterChange={onFilterChange}
            filters={filters}
            setSelectedSavedFilter={setSelectedSavedFilter}
            columnsLabels={columnsLabels}
            lists={filtersComponent?.list}
            savedFilters={savedFilters}
            setSavedFilters={setSavedFilters}
            savedFiltersKey={savedFiltersKey}
            itemIndex={index}
            userId={userId}
          />
        ))}
      {savedFilters && savedFilters?.length > 4 && (
        <>
          {savedFiltersExpanded &&
            savedFilters?.slice(4)?.map((item, index) => (
              <SavedFilter
                // eslint-disable-next-line react/no-array-index-key
                key={`${item.name}-${index}`}
                item={item}
                selectedSavedFilter={selectedSavedFilter}
                onSavedFilterClick={onSavedFilterClick}
                onFilterChange={onFilterChange}
                filters={filters}
                setSelectedSavedFilter={setSelectedSavedFilter}
                columnsLabels={columnsLabels}
                lists={filtersComponent?.list}
                savedFilters={savedFilters}
                setSavedFilters={setSavedFilters}
                savedFiltersKey={savedFiltersKey}
                itemIndex={index}
                userId={userId}
              />
            ))}
          <button
            type="button"
            className="expand-saved-filters"
            aria-label="Expand saved filters"
            onClick={() => setSavedFilterExpanded(!savedFiltersExpanded)}
          >
            {savedFiltersExpanded ? 'Collapse' : <IconDots />}
          </button>
        </>
      )}

      {savedFiltersKey &&
        !selectedSavedFilter &&
        Object.keys(filters)?.length > 0 && (
          <ClickOutsideDetector
            setOptionsListVisible={setSaveFilterMenuVisible}
            className="save-filters-menu-wrapper"
          >
            <>
              <button
                type="button"
                className="save-filters"
                onClick={() => setSaveFilterMenuVisible(!saveFilterMenuVisible)}
              >
                <div className="save-filters-button-content">Save filter</div>
                <div className="save-filters-button-content">
                  {saveFilterMenuVisible ? <IconUp /> : <IconDown />}
                </div>
              </button>
              {saveFilterMenuVisible && (
                <div className="save-filter-menu">
                  <div className="save-filter-menu-title">
                    Select a filter preset
                  </div>
                  <button
                    type="button"
                    className="save-filter-menu-button"
                    onClick={() => {
                      setSaveFilterMenuVisible(false);
                      setSaveFilterPopupVisible(true);
                    }}
                  >
                    <div className="save-filter-menu-button-content">
                      <IconExpand />
                    </div>
                    <div className="save-filter-menu-button-content with-padding">
                      Save as New Preset
                    </div>
                  </button>
                  {savedFilters &&
                    savedFilters?.length > 0 &&
                    savedFilters?.map((filterItem) => (
                      <button
                        key={filterItem?.id}
                        type="button"
                        className="save-filter-menu-button"
                        onClick={() => {
                          setSaveFilterMenuVisible(false);
                          onRewriteFilter(filterItem.id);
                        }}
                      >
                        <div className="save-filter-menu-button-content">
                          {filterItem?.name}
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </>
          </ClickOutsideDetector>
        )}
    </>
  );
}
